import { apiFetch, isDemoMode } from "./api";
import type { DeliveryStatus } from "./api-broadcasts";

/** Automations (weekly newsletter on autopilot) and Design Studio templates. */

export interface AutomationRun {
  automation_id: string;
  campaign_id: string | null;
  status: "created" | "scheduled" | "skipped" | "failed";
  detail: string | null;
  ran_at: string;
}

export interface Automation {
  id: string;
  name: string;
  status: "active" | "paused";
  template_id: string;
  template_title: string | null;
  categories: string[];
  subject: string;
  weekday: number;
  send_time: string;
  timezone: string;
  approval: "required" | "auto";
  next_run_at: string | null;
  last_run_at: string | null;
  runs: AutomationRun[];
}

export interface AutomationInput {
  name: string;
  template_id: string;
  categories: string[];
  subject: string;
  weekday: number;
  send_time: string;
  approval: "required" | "auto";
  status?: "active" | "paused";
}

export interface Template {
  id: string;
  title: string;
  subject: string;
  preheader: string | null;
  sections: unknown[];
  categories: string[];
  status: string;
  updated_at: string;
}

/** A failed call, with the migration it is waiting for when that is the reason. */
export class ApiError extends Error {
  constructor(message: string, readonly pendingMigration?: string) { super(message); }
}

async function call<T>(path: string, init?: RequestInit): Promise<{ data: T; meta: { delivery?: DeliveryStatus } }> {
  const r = await apiFetch(path, init);
  const body = (await r.json().catch(() => ({}))) as {
    data?: T; meta?: { delivery?: DeliveryStatus }; error?: string; pending_migration?: string;
  };
  if (!r.ok) throw new ApiError(body.error || `Request failed (${r.status})`, body.pending_migration);
  return { data: body.data as T, meta: body.meta ?? {} };
}

const send = (method: string, body: unknown): RequestInit => ({
  method, headers: { "content-type": "application/json" }, body: JSON.stringify(body),
});

export async function fetchAutomations() {
  if (await isDemoMode()) return { data: [] as Automation[], meta: {} };
  return call<Automation[]>("/api/v1/automations");
}
export const createAutomation = (input: AutomationInput) => call<Automation>("/api/v1/automations", send("POST", input));
export const updateAutomation = (id: string, patch: Partial<AutomationInput>) =>
  call<Automation>(`/api/v1/automations/${id}`, send("PATCH", patch));
export const deleteAutomation = (id: string) => call<{ id: string }>(`/api/v1/automations/${id}`, { method: "DELETE" });
export const runAutomationNow = (id: string) =>
  call<{ status: string; detail: string; campaign_id?: string }>(`/api/v1/automations/${id}/run`, send("POST", {}));

export async function fetchTemplates() {
  if (await isDemoMode()) return { data: [] as Template[], meta: {} };
  return call<Template[]>("/api/v1/templates");
}
export const setTemplate = (newsletterId: string, isTemplate: boolean) =>
  call<Template>("/api/v1/templates", send("POST", { newsletter_id: newsletterId, is_template: isTemplate }));
export const copyTemplate = (id: string) => call<{ id: string }>(`/api/v1/templates/${id}/use`, send("POST", {}));

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
